"use client"

import {useState} from "react"
import {FormProvider, useForm} from "react-hook-form"
import {z} from "zod"
import {zodResolver} from "@/app/lib/zodResolver"
import AppSelect from "@/app/components/atomic/AppSelect"
import AppButton from "@/app/components/atomic/molecules/AppButton"
import SectionTitle from "@/app/components/atomic/molecules/SectionTitle"
import {CONTACT_SUBJECTS} from "@/app/constants/contact-subjects"
import {postContactForm} from "@/app/endpoints/contactEndpoint"
import ChevronIcon from "@/app/components/homepage/components/ChevronIcon"
import Link from "next/link"
import {Asterisk, ContactTextField, FieldError} from "@/app/components/contact/ContactFormFields"

const contactSchema = z.object({
    lastName: z.string().min(2, "Veuillez indiquer votre nom").max(35, "35 caractères maximum"),
    firstName: z.string().min(2, "Veuillez indiquer votre prénom").max(35, "35 caractères maximum"),
    email: z.string().min(1, "Veuillez indiquer votre adresse mail").email("Votre email est invalide"),
    message: z.string().min(20, "Votre message doit contenir au moins 20 caractères"),
    consent: z.boolean().refine((v) => v === true, "Veuillez accepter pour pouvoir envoyer votre demande"),
})

type ContactFormValues = z.infer<typeof contactSchema>

const FIELD_ORDER = ["lastName", "firstName", "email", "message", "consent"] as const

const ERROR_MESSAGE = "Une erreur est survenue lors du traitement de votre question, veuillez réessayer."

export default function ContactForm() {
    const [selectedId, setSelectedId] = useState<string>(CONTACT_SUBJECTS[0].id)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [hasBackError, setHasBackError] = useState(false)
    const [showPrivacy, setShowPrivacy] = useState(false)

    const form = useForm<ContactFormValues>({
        mode: "onSubmit",
        resolver: zodResolver(contactSchema),
        defaultValues: {lastName: "", firstName: "", email: "", message: "", consent: false},
    })
    const {register, formState: {errors}} = form

    const subject = CONTACT_SUBJECTS.find((s) => s.id === selectedId) ?? CONTACT_SUBJECTS[0]
    const showForm = subject.displayForm && !isSubmitted

    const onSubmit = form.handleSubmit(
        async ({consent, ...values}) => {
            if (isSubmitting) return
            setHasBackError(false)
            setIsSubmitting(true)
            try {
                const response = await postContactForm({
                    ...values,
                    subject: subject.subject,
                    question: subject.question,
                    subjectData: {
                        value: subject.subject,
                        label: subject.label,
                        info: subject.info,
                        question: subject.question,
                    },
                })
                if (response.ok) {
                    setIsSubmitted(true)
                    form.reset()
                } else {
                    setHasBackError(true)
                }
            } catch {
                setHasBackError(true)
            } finally {
                setIsSubmitting(false)
            }
        },
        (fieldErrors) => {
            const firstErrorField = FIELD_ORDER.find((name) => fieldErrors[name])
            if (!firstErrorField) return
            const element = document.querySelector<HTMLElement>(`[name="${firstErrorField}"]`)
            element?.scrollIntoView({behavior: "smooth", block: "center"})
            element?.focus({preventScroll: true})
        }
    )

    return (
        <div className="flex flex-col gap-rm-20">
            <div className="flex flex-col gap-2">
                <SectionTitle
                    level="h1"
                    theme="white"
                    topText={{text: "", icon: "fa-phone text-lg"}}
                >
                    Nous contacter
                </SectionTitle>
            </div>

            <div className="bg-white rounded-2xl shadow p-6">
                <p className="text-rm-gray pb-6">À quel sujet souhaitez-vous nous contacter ?</p>

                <div className="flex flex-col gap-2">
                    <label className="font-semibold text-sm" htmlFor="subject">Type d'assurance<Asterisk /></label>

                    <AppSelect
                        id="subject"
                        label=""
                        name="subject"
                        placeholder="Choisissez votre catégorie"
                        className="mb-4"
                        variant="v2"
                        value={selectedId}
                        options={CONTACT_SUBJECTS.map((s) => ({value: s.id, label: s.label}))}
                        onChange={(e) => {
                            setSelectedId(String(e.target.value))
                            setHasBackError(false)
                            setIsSubmitted(false)
                        }}
                    />
                </div>

                <FormProvider {...form}>
                    <form onSubmit={onSubmit} className="flex flex-col gap-rm-20" noValidate>
                        {showForm && (
                            <>
                                <div className="flex flex-col gap-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <ContactTextField<ContactFormValues> name="lastName" label="Nom" />
                                        <ContactTextField<ContactFormValues> name="firstName" label="Prénom" />
                                    </div>

                                    <ContactTextField<ContactFormValues> name="email" label="Email" />

                                    <div className="flex flex-col gap-2">
                                        <label className="font-semibold text-rm-14" htmlFor="message">Message<Asterisk /></label>
                                        <textarea
                                            id="message"
                                            placeholder="Décrivez votre demande... (Aucune donnée sensible ne doit figurer dans ce champ)"
                                            className={`h-40 p-4 rounded w-full transition-all duration-500 border bg-white
                                            ${errors.message ? "border-invalidRed" : "border-rm-gray-light hover:border-rm-gray focus-within:border-rm-gray"}`}
                                            {...register("message")}
                                        />
                                        <FieldError message={errors.message?.message} />
                                    </div>

                                    <p className="text-rm-14 text-rm-gray">* Champs obligatoire</p>
                                </div>

                                <div className="flex flex-col gap-1">
                                    <label className="flex py-5 px-2.5 border border-rm-gray-light rounded items-start gap-2 text-sm text-pale cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="mt-1 shrink-0"
                                            {...register("consent")}
                                        />
                                        <span>
                                            J'accepte d'être contacté(e) par email par Réassurez-moi afin de traiter ma demande.<Asterisk />
                                        </span>
                                    </label>
                                    <FieldError message={errors.consent?.message} />
                                </div>

                                {hasBackError && <p className="text-red-700 font-semibold text-rm-14">{ERROR_MESSAGE}</p>}

                                <AppButton type="submit" arrowPosition="before" disabled={isSubmitting} wFull>
                                    {isSubmitting ? "Envoi en cours…" : "Envoyer ma demande"}
                                </AppButton>
                            </>
                        )}

                        {isSubmitted && (
                            <p className="text-green-600 font-semibold">
                                Votre message a bien été envoyé. Nous vous répondrons dans les meilleurs délais.
                            </p>
                        )}

                        <div className="flex flex-col">
                            <p className="text-sm pb-1">
                                Vos données personnelles
                            </p>
                            <p className={`text-xs`}>
                                Les champs suivis d’un astérisque<Asterisk /> sont obligatoires. <br/> Réassurez-moi collecte vos données personnelles pour analyser votre demande et permettre la souscription éventuelle d’un contrat auprès de ses partenaires, sur la base de son intérêt légitime et/ou de
                                mesures précontractuelles {showPrivacy ? "." : "…"}
                            </p>
                            <div
                                className={`grid transition-[grid-template-rows] duration-500 ease-in-out ${showPrivacy ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                            >
                                <div className="overflow-hidden">
                                    <p className="text-xs">
                                        Vous disposez d’un droit d’accès, de rectification, de suppression, d’opposition et de limitation.
                                        Lorsque le traitement repose sur votre consentement, vous pouvez le retirer à tout moment. Pour exercer vos droits en vous
                                        adressant à <a href="mailto:dpo@reassurez-moi.fr" className="text-rm-blue underline">dpo@reassurez-moi.fr</a> ou à DPO,
                                        Réassurez-moi, 19 Rue d'Aumale, 75009 Paris Vos données seront transmises à nos partenaires pour poursuivre le traitement de
                                        votre demande. Pour en savoir plus sur la gestion de vos données personnelles, veuillez consulter notre politique de
                                        protection des données disponible ici :
                                        <Link href={"/guide/politique-confidentialite"} className="text-rm-blue underline pl-1">
                                            https://reassurez-moi.fr/guide/politique-confidentialite
                                        </Link>
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowPrivacy((v) => !v)}
                                className="flex items-center gap-1 self-start text-xs underline"
                            >
                                {showPrivacy ? "Réduire" : "Développer"}
                                <ChevronIcon open={showPrivacy} />
                            </button>
                        </div>
                    </form>
                </FormProvider>
            </div>

            <div className="flex border border-rm-blue rounded-2xl bg-rm-blue/10 p-4 gap-4">
                <i className="fa-regular fa-info-circle mr-1 text-rm-blue text-lg" />

                <div className="flex flex-col text-pale">
                    <p className="text-lg font-semibold">Le saviez vous ?</p>
                    <div className="[&_a]:text-rm-blue [&_a]:underline" dangerouslySetInnerHTML={{__html: subject.info}} />
                </div>
            </div>
        </div>
    )
}
